import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import api, { setAccessToken, onAuthLost, onAccountBlocked } from "../api/client";

const AuthContext = createContext(null);

/**
 * Holds the signed-in user, their plan and their remaining allowance.
 *
 * On mount it attempts a silent refresh: the access token is deliberately not
 * persisted, so a page reload has nothing in memory, and the httpOnly refresh
 * cookie is the only thing that can restore the session.
 */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [entitlements, setEntitlements] = useState(null);
  const [referral, setReferral] = useState(null);
  const [ready, setReady] = useState(false);
  // Set only when the server reports the account itself is suspended/banned
  // (not a plain expired session) - the Shell shows a modal for this rather
  // than silently bouncing to the login screen.
  const [blockedInfo, setBlockedInfo] = useState(null);

  const applyAuth = useCallback((payload) => {
    setAccessToken(payload.accessToken);
    setUser(payload.user);
    setEntitlements(payload.entitlements || null);
  }, []);

  const signOutLocal = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setEntitlements(null);
    setReferral(null);
  }, []);

  useEffect(() => onAuthLost(signOutLocal), [signOutLocal]);
  useEffect(() => onAccountBlocked(setBlockedInfo), []);
  const clearBlockedInfo = useCallback(() => setBlockedInfo(null), []);

  useEffect(() => {
    // The server sets a readable, secret-free marker next to the httpOnly
    // refresh cookie. Without it there is certainly no session, so an
    // anonymous visitor skips a round trip that could only ever 401.
    if (!document.cookie.split("; ").some((c) => c.startsWith("foliox_session="))) {
      setReady(true);
      return undefined;
    }

    let cancelled = false;
    api
      .refresh()
      .then((payload) => {
        if (!cancelled) applyAuth(payload);
      })
      .catch(() => {
        /* the marker outlived the session; render signed-out */
      })
      .finally(() => {
        if (!cancelled) setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [applyAuth]);

  /** Re-reads plan, credits and referral standing after anything that spends. */
  const refreshProfile = useCallback(async () => {
    const data = await api.get("/api/users/me");
    setUser(data.user);
    setEntitlements(data.entitlements);
    setReferral(data.referral);
    return data;
  }, []);

  const login = useCallback(
    async (identifier, password) => {
      const payload = await api.post("/api/auth/login", { identifier, password });
      applyAuth(payload);
      return payload;
    },
    [applyAuth]
  );

  const register = useCallback(
    async (input) => {
      const payload = await api.post("/api/auth/register", input);
      applyAuth(payload);
      return payload;
    },
    [applyAuth]
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout");
    } catch (err) {
      /* the local session is dropped either way */
    }
    signOutLocal();
  }, [signOutLocal]);

  const value = useMemo(
    () => ({
      user,
      entitlements,
      referral,
      ready,
      isAuthed: !!user,
      isStaff: user?.role === "staff" || user?.role === "admin",
      isAdmin: user?.role === "admin",
      plan: entitlements?.plan || null,
      login,
      register,
      logout,
      refreshProfile,
      setEntitlements,
      blockedInfo,
      clearBlockedInfo,
    }),
    [
      user,
      entitlements,
      referral,
      ready,
      login,
      register,
      logout,
      refreshProfile,
      blockedInfo,
      clearBlockedInfo,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

export default AuthContext;
